import servicePages from "./data/servicePages";

export const SITE_URL = "https://thealphacpas.com";
export const SITE_NAME = "The Alpha CPAs";
export const OG_IMAGE = `${SITE_URL}/og-image.png`;
export const LOGO_URL = `${SITE_URL}/logo-512.png`;
export const LASTMOD = "2026-10-24";

const core = {
  "/": {
    name: "Home",
    title: "Tax, Audit & Accounting Support for CPA Firms | Alpha CPAs",
    description:
      "The Alpha CPAs gives CPA firms and businesses tax, audit and accounting support: bookkeeping, payroll, sales tax and advisory from Bellevue, WA.",
  },
  "/about": {
    name: "About",
    title: "About The Alpha CPAs | Accounting Firm in Bellevue, WA",
    description:
      "Learn how The Alpha CPAs has supported CPA firms and businesses with tax, audit, accounting and advisory work since 2019.",
  },
  "/services": {
    name: "Services",
    title: "Accounting, Tax & Payroll Services | The Alpha CPAs",
    description:
      "Bookkeeping, payroll, sales tax, tax preparation and CFO advisory for US businesses, plus outsourced support for CPA firms and bookkeepers.",
  },
  "/expertise": {
    name: "Expertise",
    title: "Financial Reporting & Management Insights | Alpha CPAs",
    description:
      "Customized financial reporting, cash flow analysis, P&L segmentation and management reports from The Alpha CPAs.",
  },
  "/team": {
    name: "Leadership",
    title: "Leadership Team | The Alpha CPAs Accounting Advisors",
    description:
      "Meet the partners and directors who lead The Alpha CPAs and run our tax, audit, accounting and advisory work.",
  },
  "/contact": {
    name: "Contact",
    title: "Contact The Alpha CPAs | Bellevue, WA Accounting Firm",
    description:
      "Call +1 425 221 6660 or write to The Alpha CPAs. Headquarters in Bellevue, WA with offices in Surat and Ahmedabad, India.",
  },
};

export const pageMeta = { ...core };
servicePages.forEach((p) => {
  pageMeta[`/services/${p.slug}`] = {
    name: p.name,
    title: p.title,
    description: p.description,
    service: p,
  };
});

export const notFoundMeta = {
  name: "Page not found",
  title: "Page Not Found | The Alpha CPAs",
  description: "The page you requested could not be found on thealphacpas.com.",
};

export const normalizePath = (pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  return path;
};

export const getMeta = (pathname) => pageMeta[normalizePath(pathname)] || null;

const orgId = `${SITE_URL}/#organization`;

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService", "AccountingService"],
  "@id": orgId,
  name: SITE_NAME,
  alternateName: "Alpha Accountants",
  url: `${SITE_URL}/`,
  logo: LOGO_URL,
  image: OG_IMAGE,
  description:
    "Tax, audit and accounting support for CPA firms, plus bookkeeping, payroll, sales tax and advisory services for businesses and individuals.",
  telephone: "+1-425-221-6660",
  email: "connectus@thealphacpas.com",
  foundingDate: "2019",
  areaServed: { "@type": "Country", name: "United States" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "136 102nd Ave SE Apt 524",
    addressLocality: "Bellevue",
    addressRegion: "WA",
    postalCode: "98004",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-425-221-6660",
    email: "connectus@thealphacpas.com",
    contactType: "customer service",
    areaServed: "US",
    availableLanguage: "English",
  },
  location: [
    {
      "@type": "Place",
      name: "Surat office",
      address: {
        "@type": "PostalAddress",
        streetAddress: "215, Unique Square, Causeway, Katargam",
        addressLocality: "Surat",
        postalCode: "395004",
        addressCountry: "IN",
      },
    },
    {
      "@type": "Place",
      name: "Ahmedabad office",
      address: {
        "@type": "PostalAddress",
        streetAddress: "102, Solaris Business Hub, Naranpura",
        addressLocality: "Ahmedabad",
        postalCode: "380063",
        addressCountry: "IN",
      },
    },
  ],
  sameAs: ["https://www.linkedin.com/company/the-alphaaccountants/"],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  publisher: { "@id": orgId },
  inLanguage: "en-US",
};

const crumbsFor = (path) => {
  if (path === "/") return [];
  const crumbs = [{ name: "Home", path: "/" }];
  if (path.startsWith("/services/")) {
    crumbs.push({ name: "Services", path: "/services" });
  }
  crumbs.push({ name: pageMeta[path].name, path });
  return crumbs;
};

export const getBreadcrumbs = (pathname) => {
  const path = normalizePath(pathname);
  return pageMeta[path] ? crumbsFor(path) : [];
};

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE_URL}${c.path === "/" ? "/" : c.path}`,
  })),
});

const serviceSchema = (p) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/services/${p.slug}#service`,
  name: p.name,
  serviceType: p.name,
  description: p.description,
  url: `${SITE_URL}/services/${p.slug}`,
  provider: { "@id": orgId },
  areaServed: { "@type": "Country", name: "United States" },
});

const faqSchema = (p) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: p.faqs.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const schemasFor = (pathname) => {
  const path = normalizePath(pathname);
  const meta = pageMeta[path];
  const list = [organizationSchema, websiteSchema];
  if (!meta) return list;
  const crumbs = crumbsFor(path);
  if (crumbs.length) list.push(breadcrumbSchema(crumbs));
  if (meta.service) {
    list.push(serviceSchema(meta.service));
    list.push(faqSchema(meta.service));
  }
  return list;
};
